﻿import "./global/extensions";
import { getCookieValue, setCookieValue } from "./global/preferences";

import Chart from 'chart.js/auto';

namespace RemaxMiracleSystem.Pages.Dashboard {
	interface ChartColors {
		strokeColor: string;
		fillColor: string;
		pointHighlightFill: string;
	}

	const HonorCardChartColorsByOrder = <Array<ChartColors>>[];
	HonorCardChartColorsByOrder[0] = {
		strokeColor: "rgba(67,160,3,1)",
		fillColor: "rgba(203,218,193,0.3)",
		pointHighlightFill: "#80d04b"
	};
	HonorCardChartColorsByOrder[1] = {
		strokeColor: "rgba(53,64,76,1)",
		fillColor: "rgba(193,210,218,0.3)",
		pointHighlightFill: "#4baed0"
	};

	interface HonorCardChartViewModel extends LineChartViewModel {
		totalCreatedYTD: number;
	}

	interface DonationChartViewModel extends LineChartViewModel {
		totalAmountYTD: number;
	}

	interface LineChartViewModel {
		labels: Array<string>;
		lines: Array<LineChartData>;
	}

	interface LineChartData {
		points: Array<number>;
	}

	interface LineChartDataSet extends ChartColors {
		values: Array<number>;
	}

	interface ChartElement {
		caretHeight: number;
		caretPadding: number;
		chart: any;
		cornerRadius: number;
		custom: (tooltip: ChartElement | boolean) => void;
		fillColor: string;
		fontFamily: string;
		fontSize: number;
		fontStyle: string;
		labels: string[] | undefined;
		text: string | undefined;
		textColor: string;
		x: number;
		xAlign: string | undefined;
		xPadding: number;
		xOffset: number | undefined;
		y: number;
		yAlign: string | undefined;
		yPadding: number;
		yOffset: number | undefined;
		width: number;
		height: number;
	}

	interface FailedPaymentInfo {
		paymentFailureReason: string;
		paymentId: number;
		paymentNumber: string;
	}
	
	interface CartExpiring {
		upcomingCartExpiring: boolean;
		clearedDate: Date;
	}

	export class ViewModel {
		constructor(paymentDetailsUrl: string | undefined) {
			this.overrideChartDefaults();

			this.charts = [];

			$.getJSON("dashboard/failed-payments", (failedPayments: Array<FailedPaymentInfo>) => {
				if (failedPayments && failedPayments.length) {
					$('#failed-payments-modal').toggleClass("pluralized", failedPayments.length > 1);

					var paymentLinksHtml = "";
					$.each(failedPayments, (i, p) => {
						if (paymentDetailsUrl) {
							paymentLinksHtml += `<li><a href="${paymentDetailsUrl.replace('/0', '/' + p.paymentId)}" target="_blank">Payment ${p.paymentNumber} - ${p.paymentFailureReason}</a></li>`;
						}
					});
					$('#failed-payments-modal-list').html(paymentLinksHtml);

					$('#failed-payments-modal').modal({
						backdrop: 'static',
						keyboard: false,
						show: false
					});
				}
			});

			$.getJSON("dashboard/charts", (data: { donationChart: DonationChartViewModel, honorCardChart: HonorCardChartViewModel }) => {
				$('#donations-chart-label').html(`${Number.toMoney(data.donationChart.totalAmountYTD)} Total Fundraising`);
				this.charts.push(new LineChart(
					'donations-chart',
					data.donationChart.labels,
					$.map(data.donationChart.lines, (l, i) => <LineChartDataSet>{
						strokeColor: '#f9735b',
						fillColor: 'rgba(249,115,91,0.1)',
						pointHighlightFill: '#253746',
						values: l.points
					}),
					'#253746',
					value => {
						if (!(value + "").match(/\s+/g) && !isNaN(<any>value)) {
							var tmp = parseFloat(("" + value).replace(/[^0-9-\.]/g, ""));
							return tmp.formatMoney(2);
						}

						return value;
					}
				));
				$('#honor-cards-chart-label').html(`${data.honorCardChart.totalCreatedYTD} Honor Card${data.honorCardChart.totalCreatedYTD === 1 ? '' : 's'} Created`);
				this.charts.push(new LineChart(
					'honor-cards-chart',
					data.honorCardChart.labels,
					$.map(data.honorCardChart.lines, (l, i) => <LineChartDataSet>{
						strokeColor: HonorCardChartColorsByOrder[i].strokeColor,
						fillColor: HonorCardChartColorsByOrder[i].fillColor,
						pointHighlightFill: HonorCardChartColorsByOrder[i].pointHighlightFill,
						values: l.points
					}),
					HonorCardChartColorsByOrder[0].pointHighlightFill
				));
			});
			
			const cartExpiringModalDisplayed = getCookieValue('cartExpiringModalDisplayed');
			
			if (!cartExpiringModalDisplayed) {
				$.get("dashboard/cart-expiring", (data: CartExpiring) => {
					if (data.upcomingCartExpiring) {
						setCookieValue('cartExpiringModalDisplayed', 'true', 1);
						$("#clearedDate").html(new Date(data.clearedDate).toDateString());
						$('#expiration-modal').modal({
							backdrop: 'static',
							keyboard: false,
							show: true
						});
					}
				});
			}
		}

		public charts: Array<LineChart>;

		private overrideChartDefaults(): void {
			Chart.defaults.animation = true;
			Chart.defaults.scaleLineColor = "transparent";
			Chart.defaults.scaleLineWidth = 0;
			Chart.defaults.scaleBeginAtZero = true;
			Chart.defaults.scaleFontFamily = "'proxima-nova', sans-serif";
			Chart.defaults.scaleFontColor = "#253746";
			Chart.defaults.responsive = true;
			Chart.defaults.tooltipFillColor = "rgba(75,174,208,1)";
			Chart.defaults.tooltipFontFamily = "'proxima-nova', sans-serif";
			Chart.defaults.tooltipTitleFontFamily = "'proxima-nova', sans-serif";
			Chart.defaults.tooltipFontSize = 12;
			Chart.defaults.tooltipCornerRadius = 12;
			Chart.defaults.tooltipCaretSize = 4;
		}
	}

	class LineChart {
		private _parentElement: JQuery;
		private _canvasElement: JQuery;
		private _tooltipElement: JQuery;

		constructor(canvasElementId: string, labels: Array<string>, lines: Array<LineChartDataSet>, tooltipColor: string, textFormatter?: (value: string) => string) {
			this._canvasElement = $("#" + canvasElementId);
			this._parentElement = this._canvasElement.parent();
			this._tooltipElement = $('<div class="line-chart-tooltip"></div>');
			this._parentElement.append(this._tooltipElement);

			var ctx = (<HTMLCanvasElement>this._canvasElement.get(0)).getContext("2d");
			var data = this.createData(labels, lines);
			var lineChartOptions = {
				scaleGridLineColor: "rgba(194, 211, 219, 1)",
				scaleShowVerticalLines: true,
				customTooltips: (tooltip: ChartElement | boolean) => {
					if (!tooltip) {
						this._tooltipElement
							.css("display", "none");
						return;
					}
					if (typeof tooltip !== "boolean") {
						var text = (textFormatter && textFormatter(tooltip.text)) || (tooltip.labels && tooltip.labels.length && tooltip.labels.join(",&nbsp;")) || tooltip.text;
						this._tooltipElement.html('<div class="arrow" style="border-color: ' + tooltipColor + ' transparent transparent transparent"></div><div class="line-chart-tooltip-size">' + text + '</div><div class="line-chart-tooltip-label">' + text + '</div>');
						this._tooltipElement.css({
							// These offsets are to fix some weird tooltip placement bugs with multiple lines
							left: tooltip.x - (tooltip.x % 1 === 0 ? tooltip.xOffset || 0 : (tooltip.labels ? -66 : 0)),
							top: tooltip.y - (tooltip.labels ? (tooltip.y === (tooltip.height / 2) ? 28 : 0) : 15),
							display: "block",
							"background-color": tooltipColor
						});
					}
				}
			};
			
			var chartOptions = this.createChartOptions(tooltipColor, textFormatter);
			var chart = new Chart(ctx, {type: 'line', data: data, options: chartOptions});

			$("body").on("toggled.sidebar.cmn", function () {
                chart.update();
			});
		}
		
		private createData(labels: Array<string>, lines: Array<LineChartDataSet>): any {
			return {
				labels: labels,
				datasets: [{
					label: '',
					data: lines[0].values,
					borderWidth: 2,
					borderColor: lines[0].strokeColor,
					fill: true,
					lineTension: 0.4,
					backgroundColor: lines[0].fillColor,
					pointBorderColor: lines[0].strokeColor,
					pointBackgroundColor: '#fff',
					pointHoverBackgroundColor: lines[0].pointHighlightFill,
					pointRadius: 4,
				}]
			};
		}
		
		private createChartOptions(tooltipColor: string, textFormatter?: (value: string) => string): any {
			return {
                scales: {
                    y: {
                        beginAtZero: true,
                        suggestedMin: 0,
                    }
                },
				plugins: {
					legend: {
						display: false
					},
					tooltip: {
						interaction: {
							mode: 'index',
							intersect: false,
						},
						padding: 10,
						bodyAlign: 'center',
						yAlign: 'bottom',
						backgroundColor: tooltipColor,
						displayColors: false,
						titleMarginBottom: 0,
						callbacks: {
							title: function(context: any) {
								return '';
							},
							label: function(context: any) {
								let label = context.dataset.label || '';

								if (label) {
									label += ': ';
								}
								if (context.parsed.y !== null) {
									label += (textFormatter && textFormatter(context.parsed.y)) || (context.parsed.y && context.parsed.y.length && context.parsed.y.join(",&nbsp;")) || context.parsed.y;
								}
								return label;
							}
						},
					}
				}
			}
		}
	}
}

$(function () {
	const { paymentDetailsUrl } = window as any;
	new RemaxMiracleSystem.Pages.Dashboard.ViewModel(paymentDetailsUrl);
});
